import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  MenuItem,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  TextFields as TextIcon,
  List as DropdownIcon,
  VisibilityOff as HiddenIcon,
  Inventory as MaterialIcon,
  Person as EmployeeIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const AdminSettingsPage = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [departments, setDepartments] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [materialItems, setMaterialItems] = useState([]);

  // Field settings state
  const [fieldSettings, setFieldSettings] = useState([]);
  const [fieldSettingsLoading, setFieldSettingsLoading] = useState(false);
  const [fieldSettingsSaving, setFieldSettingsSaving] = useState(null); // categoryId being saved
  const [fieldSettingsSuccess, setFieldSettingsSuccess] = useState('');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Filter/Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    department_name: '',
    description: '',
    category_name: '',
    item_name: '',
    department_id: '',
    expense_category_id: '',
    unit: '',
    is_active: true
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (currentTab === 3) {
      fetchFieldSettings();
    }
  }, [currentTab]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [deptRes, catRes, itemsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/departments`, { headers }),
        axios.get(`${API_BASE_URL}/expense-categories`, { headers }),
        axios.get(`${API_BASE_URL}/material-items`, { headers })
      ]);

      setDepartments(deptRes.data.data);
      setExpenseCategories(catRes.data.data);
      setMaterialItems(itemsRes.data.data);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldSettings = async () => {
    try {
      setFieldSettingsLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/expense-categories/field-settings/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFieldSettings(res.data.data);
    } catch (err) {
      console.error('Failed to fetch field settings:', err);
    } finally {
      setFieldSettingsLoading(false);
    }
  };

  const handleFieldSettingChange = (categoryId, field, value) => {
    setFieldSettings(prev => prev.map(item => {
      if (item.expenseCategoryId === categoryId) {
        return { ...item, settings: { ...item.settings, [field]: value } };
      }
      return item;
    }));
  };

  const handleSaveFieldSettings = async (categoryId) => {
    try {
      setFieldSettingsSaving(categoryId);
      const token = localStorage.getItem('token');
      const item = fieldSettings.find(f => f.expenseCategoryId === categoryId);
      if (!item) return;

      await axios.put(
        `${API_BASE_URL}/expense-categories/${categoryId}/field-settings`,
        {
          materialItemMode: item.settings.materialItemMode,
          formType: item.settings.formType || 'material',
          quantityRequired: item.settings.quantityRequired,
          unitRequired: item.settings.unitRequired,
          supplierNameRequired: item.settings.supplierNameRequired,
          billNumberRequired: item.settings.billNumberRequired,
          amountRequired: item.settings.amountRequired,
          billUploadRequired: item.settings.billUploadRequired,
          gstSlabRequired: item.settings.gstSlabRequired
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFieldSettingsSuccess(`Settings saved for "${item.categoryName}"!`);
      setTimeout(() => setFieldSettingsSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save field settings');
    } finally {
      setFieldSettingsSaving(null);
    }
  };

  const handleOpenDialog = (type, mode, item = null) => {
    setDialogType(type);
    setDialogMode(mode);
    setEditingItem(item);

    if (mode === 'edit' && item) {
      if (type === 'department') {
        setFormData({
          department_name: item.department_name || '',
          description: item.description || '',
          is_active: item.is_active !== false
        });
      } else if (type === 'category') {
        setFormData({
          category_name: item.category_name || '',
          description: item.description || '',
          is_active: item.is_active !== false
        });
      } else if (type === 'item') {
        setFormData({
          item_name: item.itemName || item.item_name || '',
          department_id: item.departmentId || item.department_id || '',
          expense_category_id: item.expenseCategoryId || item.expense_category_id || '',
          unit: item.unit || '',
          description: item.description || '',
          is_active: item.isActive !== false && item.is_active !== false
        });
      }
    } else {
      setFormData({
        department_name: '',
        category_name: '',
        item_name: '',
        department_id: '',
        expense_category_id: '',
        unit: '',
        description: '',
        is_active: true
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (dialogType === 'department') {
        const data = {
          department_name: formData.department_name,
          description: formData.description,
          is_active: formData.is_active
        };
        if (dialogMode === 'edit') {
          await axios.put(`${API_BASE_URL}/departments/${editingItem.id}`, data, { headers });
          setSuccess('Department updated successfully!');
        } else {
          await axios.post(`${API_BASE_URL}/departments`, data, { headers });
          setSuccess('Department created successfully!');
        }
      } else if (dialogType === 'category') {
        const data = {
          category_name: formData.category_name,
          description: formData.description,
          is_active: formData.is_active
        };
        if (dialogMode === 'edit') {
          await axios.put(`${API_BASE_URL}/expense-categories/${editingItem.id}`, data, { headers });
          setSuccess('Expense category updated successfully!');
        } else {
          await axios.post(`${API_BASE_URL}/expense-categories`, data, { headers });
          setSuccess('Expense category created successfully!');
        }
      } else if (dialogType === 'item') {
        const data = {
          item_name: formData.item_name,
          department_id: parseInt(formData.department_id),
          expense_category_id: parseInt(formData.expense_category_id),
          unit: formData.unit,
          description: formData.description,
          is_active: formData.is_active
        };
        if (dialogMode === 'edit') {
          await axios.put(`${API_BASE_URL}/material-items/${editingItem.id}`, data, { headers });
          setSuccess('Material item updated successfully!');
        } else {
          await axios.post(`${API_BASE_URL}/material-items`, data, { headers });
          setSuccess('Material item created successfully!');
        }
      }

      handleCloseDialog();
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Are you sure you want to deactivate "${name}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (type === 'department') {
        await axios.delete(`${API_BASE_URL}/departments/${id}`, { headers });
        setSuccess('Department deactivated successfully!');
      } else if (type === 'category') {
        await axios.delete(`${API_BASE_URL}/expense-categories/${id}`, { headers });
        setSuccess('Expense category deactivated successfully!');
      } else if (type === 'item') {
        await axios.delete(`${API_BASE_URL}/material-items/${id}`, { headers });
        setSuccess('Material item deactivated successfully!');
      }

      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));
  };

  const filteredMaterialItems = materialItems.filter(item => {
    const itemName = item.itemName || item.item_name || '';
    const matchesSearch = !searchTerm || itemName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = !filterDepartment || (item.departmentId || item.department_id) === parseInt(filterDepartment);
    const matchesCat = !filterCategory || (item.expenseCategoryId || item.expense_category_id) === parseInt(filterCategory);
    return matchesSearch && matchesDept && matchesCat;
  });

  const FIELD_LABELS = {
    quantityRequired: 'Quantity',
    unitRequired: 'Unit',
    supplierNameRequired: 'Supplier Name',
    billNumberRequired: 'Bill Number',
    amountRequired: 'Amount',
    billUploadRequired: 'Bill Upload',
    gstSlabRequired: 'GST Slab'
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Admin Settings</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label={`Material Items (${materialItems.length})`} />
          <Tab label={`Expense Categories (${expenseCategories.length})`} />
          <Tab label={`Departments (${departments.length})`} />
          <Tab label="Form Field Settings" />
        </Tabs>
      </Paper>

      {loading && currentTab !== 3 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* MATERIAL ITEMS TAB */}
          {currentTab === 0 && (
            <Paper>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField select fullWidth size="small" label="Filter by Department"
                      value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
                      <MenuItem value="">All Departments</MenuItem>
                      {departments.map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>{dept.department_name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField select fullWidth size="small" label="Filter by Category"
                      value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                      <MenuItem value="">All Categories</MenuItem>
                      {expenseCategories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button fullWidth variant="contained" startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog('item', 'add')}>
                      Add Item
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMaterialItems.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No items found</TableCell></TableRow>
                    ) : (
                      filteredMaterialItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell><strong>{item.itemName || item.item_name || 'N/A'}</strong></TableCell>
                          <TableCell>
                            <Chip label={item.department?.department_name || item.department?.departmentName || '-'}
                              size="small" color={item.department?.department_name === 'Civil' ? 'primary' : 'secondary'} />
                          </TableCell>
                          <TableCell>{item.expenseCategory?.category_name || item.expenseCategory?.categoryName || '-'}</TableCell>
                          <TableCell>{item.unit || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={(item.isActive !== undefined ? item.isActive : item.is_active) ? 'Active' : 'Inactive'}
                              size="small"
                              color={(item.isActive !== undefined ? item.isActive : item.is_active) ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenDialog('item', 'edit', item)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete('item', item.id, item.itemName || item.item_name)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* EXPENSE CATEGORIES TAB */}
          {currentTab === 1 && (
            <Paper>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('category', 'add')}>
                  Add Category
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenseCategories.map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell><strong>{cat.category_name}</strong></TableCell>
                        <TableCell>{cat.description || '-'}</TableCell>
                        <TableCell>
                          <Chip label={cat.is_active ? 'Active' : 'Inactive'} size="small"
                            color={cat.is_active ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenDialog('category', 'edit', cat)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete('category', cat.id, cat.category_name)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* DEPARTMENTS TAB */}
          {currentTab === 2 && (
            <Paper>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('department', 'add')}>
                  Add Department
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Department Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departments.map((dept) => (
                      <TableRow key={dept.id}>
                        <TableCell><strong>{dept.department_name}</strong></TableCell>
                        <TableCell>{dept.description || '-'}</TableCell>
                        <TableCell>
                          <Chip label={dept.is_active ? 'Active' : 'Inactive'} size="small"
                            color={dept.is_active ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenDialog('department', 'edit', dept)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete('department', dept.id, dept.department_name)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* FORM FIELD SETTINGS TAB */}
          {currentTab === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure which fields are mandatory or optional for each expense category in the Log Material form.
              </Typography>

              {fieldSettingsSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFieldSettingsSuccess('')}>
                  {fieldSettingsSuccess}
                </Alert>
              )}

              {fieldSettingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                fieldSettings.map((item) => (
                  <Paper key={item.expenseCategoryId} sx={{ mb: 2, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>{item.categoryName}</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={fieldSettingsSaving === item.expenseCategoryId
                          ? <CircularProgress size={16} color="inherit" />
                          : <SaveIcon />}
                        onClick={() => handleSaveFieldSettings(item.expenseCategoryId)}
                        disabled={fieldSettingsSaving === item.expenseCategoryId}
                      >
                        Save
                      </Button>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Form Type */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Form Type
                      </Typography>
                      <ToggleButtonGroup
                        value={item.settings?.formType || 'material'}
                        exclusive
                        onChange={(e, val) => {
                          if (val) handleFieldSettingChange(item.expenseCategoryId, 'formType', val);
                        }}
                        size="small"
                      >
                        <ToggleButton value="material">
                          <Tooltip title="Standard material log form (Quantity, Unit, Supplier, Bill, etc.)">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MaterialIcon fontSize="small" /> Material
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="employee_expense">
                          <Tooltip title="Simplified expense form (Employee Name, Amount, Payment Source, optional Invoice)">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmployeeIcon fontSize="small" /> Employee Expense
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {(item.settings?.formType || 'material') === 'material' && (
                    <>

                    {/* Material Item Mode */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Material Item Field
                      </Typography>
                      <ToggleButtonGroup
                        value={item.settings?.materialItemMode || 'dropdown'}
                        exclusive
                        onChange={(e, val) => {
                          if (val) handleFieldSettingChange(item.expenseCategoryId, 'materialItemMode', val);
                        }}
                        size="small"
                      >
                        <ToggleButton value="dropdown">
                          <Tooltip title="Show as searchable dropdown">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DropdownIcon fontSize="small" /> Dropdown
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="both">
                          <Tooltip title="Show both dropdown and free text input">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DropdownIcon fontSize="small" /> Both
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="freetext">
                          <Tooltip title="Show as free text input">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextIcon fontSize="small" /> Free Text
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="hidden">
                          <Tooltip title="Hide this field">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <HiddenIcon fontSize="small" /> Hidden
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Boolean field toggles */}
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Required Fields
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries(FIELD_LABELS).map(([key, label]) => (
                        <Grid item xs={6} sm={4} md={3} key={key}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={item.settings?.[key] !== undefined ? item.settings[key] : true}
                                onChange={(e) => handleFieldSettingChange(item.expenseCategoryId, key, e.target.checked)}
                              />
                            }
                            label={<Typography variant="body2">{label}</Typography>}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    </>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          )}
        </>
      )}

      {/* ADD/EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {dialogMode === 'edit' ? 'Edit' : 'Add'}{' '}
            {dialogType === 'department' ? 'Department' : dialogType === 'category' ? 'Expense Category' : 'Material Item'}
          </DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {dialogType === 'department' && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Department Name *" name="department_name"
                      value={formData.department_name} onChange={handleChange} required />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Description" name="description"
                      value={formData.description} onChange={handleChange} />
                  </Grid>
                </>
              )}

              {dialogType === 'category' && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Category Name *" name="category_name"
                      value={formData.category_name} onChange={handleChange} required />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Description" name="description"
                      value={formData.description} onChange={handleChange} />
                  </Grid>
                </>
              )}

              {dialogType === 'item' && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Item Name *" name="item_name"
                      value={formData.item_name} onChange={handleChange} required
                      placeholder="e.g., Cement (Bags)" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label="Department *" name="department_id"
                      value={formData.department_id} onChange={handleChange} required>
                      {departments.map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>{dept.department_name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label="Expense Category *" name="expense_category_id"
                      value={formData.expense_category_id} onChange={handleChange} required>
                      {expenseCategories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Unit *" name="unit"
                      value={formData.unit} onChange={handleChange} required
                      placeholder="e.g., Bags, MT, Cubic Meter" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Description" name="description"
                      value={formData.description} onChange={handleChange} />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active} onChange={handleChange} name="is_active" />}
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {dialogMode === 'edit' ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminSettingsPage;
